import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { createListItems } from '@xemplo/stateful-list-core';

import { TimeInputType, TimeListOpt } from '../time-picker.types';

import {
  ALL_WHITESPACE_REGEX,
  COMPACT_TIME_WITH_PERIOD_REGEX,
  DIGIT_ONLY_TIME_REGEX,
  outputFormat,
  timeFormats,
  VALID_TIME_CHARACTERS_REGEX,
} from './time-picker.const';
dayjs.extend(customParseFormat);

export function makeTimeList(props: TimeListOpt) {
  return createListItems()
    .addGroup((group) => {
      const times = generateTimeOptions(props);
      times.forEach((time) => group.addItem(time, time));
      return group;
    })
    .build();
}

export function generateTimeOptions({
  timeInputType = '12-hour',
  startHour = 7,
  endHour = 20,
  increment = 30,
}: TimeListOpt): string[] {
  const times: string[] = [];

  for (let hour = startHour; hour <= endHour; hour++) {
    for (let min = 0; min < 60; min += increment) {
      if (hour === endHour && min > 0) break;

      const date = new Date();
      date.setHours(hour, min, 0, 0);

      const timeString = date.toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        hour12: timeInputType === '12-hour',
      });

      times.push(timeString.toLocaleUpperCase());
    }
  }

  return times;
}

/**
 * Attempts to parse a time input string using dayjs and the defined formats.
 *
 * @param input - The input time string (e.g., "830A", "13:00").
 * @param type - Whether to interpret the input as 12-hour or 24-hour time.
 * @param isStrict - Whether to use strict parsing with dayjs.
 * @returns The normalized time string if valid, or undefined.
 */
function parser(
  input: string,
  type: TimeInputType = '12-hour',
  isStrict = true
): string | undefined {
  const parsed = dayjs(input, timeFormats[type], isStrict);
  return parsed.isValid() ? parsed.format(outputFormat[type]) : undefined;
}

/**
 * Manually parses digit-only time input (e.g. "830" → 8:30 AM).
 * Used as a fallback when strict parsing fails.
 *
 * @param input - A string of digits representing time.
 * @param type - Time format (12-hour or 24-hour).
 * @returns The normalized time string or undefined if invalid.
 */
function manualParser(input: string, type: TimeInputType): string | undefined {
  const numeric = parseInt(input, 10);
  // Handle single or two digit time inputs (e.g., "7", "8", "12")
  if (numeric <= 23) {
    return dayjs().hour(numeric).minute(0).format(outputFormat[type]);
  }

  // Handle three or four digit inputs (e.g., "830", "1130", "1530")
  const hours = Math.floor(numeric / 100);
  const minutes = numeric % 100;

  if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
    return dayjs().hour(hours).minute(minutes).format(outputFormat[type]);
  }

  return undefined;
}

/**
 * Normalizes compact inputs like "830A" or "1115PM" into a format dayjs understands.
 *
 * @param input - Raw input string.
 * @returns A cleaned string (e.g., "8:30 AM") or the original input if not matched.
 */
function normalizeInput(input: string): string {
  const cleaned = input.trim().replace(ALL_WHITESPACE_REGEX, '').toUpperCase();

  // Match e.g., 830A or 1115PM or 07A
  const match = cleaned.match(COMPACT_TIME_WITH_PERIOD_REGEX);
  if (match) {
    const time = match[1];
    const period = match[2].length === 1 ? match[2] + 'M' : match[2];

    let hours = '';
    let minutes = '';

    if (time.length <= 2) {
      hours = time;
      minutes = '00';
    } else if (time.length === 3) {
      hours = time[0];
      minutes = time.slice(1);
    } else {
      hours = time.slice(0, 2);
      minutes = time.slice(2);
    }

    return `${hours}:${minutes} ${period}`;
  }

  return input.trim().toUpperCase();
}

/**
 * Parses and normalizes a flexible user-inputted time string into a standardized format.
 *
 * Supports both 12-hour and 24-hour input and handles a variety of compact or spaced formats.
 *
 * @param input - The raw input string (e.g., "7", "830", "8:30AM", "13:45").
 * @param type - Time format mode ('12-hour' or '24-hour'), defaults to '12-hour'.
 * @returns A normalized time string like "7:30 AM" or "13:45", or undefined if invalid.
 */
export function formatTimeInput(
  input: string,
  type: TimeInputType = '12-hour'
): string | undefined {
  const cleaned = normalizeInput(String(input));

  // When non-time ignores and returns undefined
  if (!VALID_TIME_CHARACTERS_REGEX.test(cleaned)) return undefined;

  const strict = parser(cleaned, type, true);
  if (strict) return strict;

  // If strict parsing fails, try manual parsing if only digits are present
  if (DIGIT_ONLY_TIME_REGEX.test(cleaned)) {
    return manualParser(cleaned, type);
  }

  return parser(cleaned, type, false);
}
